<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  approvals: {
    type: Array,
    default: () => [],
  },
  activeApprovalId: {
    type: Number,
    default: null,
  },
  canDecide: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    required: true,
  },
  emptyLabel: {
    type: String,
    required: true,
  },
  approveLabel: {
    type: String,
    required: true,
  },
  rejectLabel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['decide']);
const { t } = useI18n();

const approvalStatusLabels = {
  pending: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.PENDING'),
  approved: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.APPROVED'),
  rejected: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.REJECTED'),
  expired: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.EXPIRED'),
  cancelled: t('CAPTAIN_EXPERIENCE.APPROVAL_STATUS.CANCELLED'),
};

const runStatusLabels = {
  queued: t('CAPTAIN_EXPERIENCE.RUN_STATUS.QUEUED'),
  running: t('CAPTAIN_EXPERIENCE.RUN_STATUS.RUNNING'),
  waiting_for_approval: t('CAPTAIN_EXPERIENCE.RUN_STATUS.WAITING_FOR_APPROVAL'),
  completed: t('CAPTAIN_EXPERIENCE.RUN_STATUS.COMPLETED'),
  failed: t('CAPTAIN_EXPERIENCE.RUN_STATUS.FAILED'),
  cancelled: t('CAPTAIN_EXPERIENCE.RUN_STATUS.CANCELLED'),
};

const approvalOutcomeLabels = {
  pending: t('CAPTAIN_EXPERIENCE.APPROVAL_OUTCOME.PENDING'),
  approved_and_executed: t(
    'CAPTAIN_EXPERIENCE.APPROVAL_OUTCOME.APPROVED_AND_EXECUTED'
  ),
  approved_but_failed: t(
    'CAPTAIN_EXPERIENCE.APPROVAL_OUTCOME.APPROVED_BUT_FAILED'
  ),
  rejected: t('CAPTAIN_EXPERIENCE.APPROVAL_OUTCOME.REJECTED'),
};

const approvalStatusLabel = approval =>
  approvalStatusLabels[approval.status] ||
  approval.status_label ||
  approval.status;

const actionResultStatusLabel = actionResult =>
  runStatusLabels[actionResult?.status] ||
  actionResult?.status_label ||
  actionResult?.status;

const approvalOutcomeLabel = approval =>
  approvalOutcomeLabels[approval.decision_outcome] || approval.decision_outcome;

const approvalExecutionSucceeded = approval =>
  approval.decision_outcome === 'approved_and_executed';
</script>

<template>
  <div class="flex flex-col gap-3 pt-3">
    <h2 class="text-base font-medium text-n-slate-12">
      {{ title }}
    </h2>
    <div v-if="approvals.length" class="flex flex-col gap-2">
      <article
        v-for="approval in approvals"
        :key="approval.id"
        class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="text-sm font-medium text-n-slate-12">
              {{ approval.requested_action }}
            </div>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ approval.reason || approvalStatusLabel(approval) }}
            </p>
          </div>
          <div
            v-if="canDecide && approval.status === 'pending'"
            class="flex flex-shrink-0 gap-2"
          >
            <Button
              size="xs"
              color="slate"
              variant="faded"
              :label="rejectLabel"
              :is-loading="activeApprovalId === approval.id"
              @click="emit('decide', approval, 'reject')"
            />
            <Button
              size="xs"
              :label="approveLabel"
              :is-loading="activeApprovalId === approval.id"
              @click="emit('decide', approval, 'approve')"
            />
          </div>
        </div>
        <div
          v-if="
            approval.action_result ||
            (approval.decision_outcome &&
              approval.decision_outcome !== 'pending')
          "
          class="mt-2 rounded-md px-2 py-1.5 text-xs"
          :class="
            approvalExecutionSucceeded(approval)
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-ruby-3 text-n-ruby-11'
          "
        >
          {{
            approval.action_result?.error_message ||
            approval.action_result?.result ||
            approvalOutcomeLabel(approval) ||
            actionResultStatusLabel(approval.action_result)
          }}
        </div>
      </article>
    </div>
    <p v-else class="text-sm text-n-slate-11">
      {{ emptyLabel }}
    </p>
  </div>
</template>
